import { Link } from 'react-router-dom';

export const studentToneClass = {
  accent: 'bg-[#fce7f3] text-[#b91c66] ring-1 ring-[#f4b6d2]',
  soft: 'bg-stone-100 text-stone-700 ring-1 ring-stone-200',
  warning: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  success: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
};

export const StudentPage = ({ title, subtitle, action, children, className = '' }) => (
  <div className={`w-full space-y-4 pb-8 sm:space-y-5 ${className}`}>
    <div className="rounded-[28px] border border-stone-200 bg-white px-4 py-4 shadow-[0_18px_40px_-34px_rgba(28,25,23,0.3)] sm:rounded-none sm:border-0 sm:bg-transparent sm:px-1 sm:py-0 sm:shadow-none">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 sm:text-[11px] sm:tracking-[0.18em]">
          Student workspace
          </p>
          <h1 className="text-[2rem] font-semibold leading-none tracking-tight text-stone-950 sm:text-4xl">
            {title}
          </h1>
          {subtitle ? (
            <p className="max-w-3xl text-sm leading-6 text-stone-600">{subtitle}</p>
          ) : null}
        </div>
        {action ? <div className="sm:pt-1 [&>*]:w-full sm:[&>*]:w-auto">{action}</div> : null}
      </div>
    </div>
    {children}
  </div>
);

export const StudentHero = ({
  eyebrow,
  title,
  body,
  primaryAction,
  secondaryAction,
  meta,
}) => (
  <section className="rounded-[32px] border border-stone-200 bg-white px-5 py-5 shadow-[0_20px_48px_-34px_rgba(28,25,23,0.3)] sm:px-6 sm:py-6">
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_20rem] lg:items-start">
      <div className="space-y-4">
        {eyebrow ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500">
            {eyebrow}
          </p>
        ) : null}
        <div className="space-y-2">
          <h2 className="text-3xl font-semibold tracking-tight text-stone-950">
            {title}
          </h2>
          {body ? (
            <p className="max-w-2xl text-sm leading-6 text-stone-600">{body}</p>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          {primaryAction}
          {secondaryAction}
        </div>
      </div>
      {meta ? (
        <div className="rounded-[24px] border border-stone-200 bg-stone-50 p-4">
          {meta}
        </div>
      ) : null}
    </div>
  </section>
);

export const StudentGridStats = ({ children }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{children}</div>
);

export const StudentStatCard = ({ label, value, detail, tone = 'soft' }) => (
  <div className="rounded-[28px] border border-stone-200 bg-white px-5 py-5 shadow-[0_16px_36px_-30px_rgba(28,25,23,0.3)]">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-stone-500">
          {label}
        </p>
        <p className="mt-3 text-3xl font-semibold tracking-tight text-stone-950">
          {value}
        </p>
      </div>
      <span
        className={`mt-1 block h-3 w-3 rounded-full ${
          tone === 'accent'
            ? 'bg-[#d61f74]'
            : tone === 'warning'
            ? 'bg-amber-400'
            : tone === 'success'
            ? 'bg-emerald-500'
            : 'bg-stone-300'
        }`}
      />
    </div>
    {detail ? <p className="mt-3 text-sm leading-6 text-stone-600">{detail}</p> : null}
  </div>
);

export const StudentSection = ({ title, description, action, children, className = '' }) => (
  <section
    className={`rounded-[32px] border border-stone-200 bg-white px-5 py-5 shadow-[0_20px_48px_-34px_rgba(28,25,23,0.3)] sm:px-6 sm:py-6 ${className}`}
  >
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="space-y-1">
        <h3 className="text-xl font-semibold tracking-tight text-stone-950">{title}</h3>
        {description ? <p className="text-sm leading-6 text-stone-600">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

export const StudentCard = ({ children, className = '' }) => (
  <div className={`rounded-[26px] border border-stone-200 bg-stone-50 p-4 sm:p-5 ${className}`}>
    {children}
  </div>
);

export const StudentBadge = ({ tone = 'soft', children }) => (
  <span
    className={`inline-flex min-h-[32px] items-center rounded-full px-3 text-xs font-semibold ${studentToneClass[tone] || studentToneClass.soft}`}
  >
    {children}
  </span>
);

export const StudentProgressBar = ({ value }) => (
  <div className="space-y-2">
    <div className="h-2.5 overflow-hidden rounded-full bg-stone-200">
      <div
        className="h-full rounded-full bg-[#d61f74] transition-all"
        style={{ width: `${Math.max(0, Math.min(Number(value || 0), 100))}%` }}
      />
    </div>
    <p className="text-xs font-medium text-stone-500">{value}% complete</p>
  </div>
);

export const StudentActionLink = ({
  to,
  children,
  variant = 'primary',
  className = '',
}) => {
  const base =
    variant === 'secondary'
      ? 'border border-stone-300 bg-white text-stone-700 hover:bg-stone-50'
      : 'bg-[#d61f74] text-white hover:bg-[#b91c66] shadow-[0_12px_30px_-18px_rgba(214,31,116,0.55)]';

  return (
    <Link
      to={to}
      className={`inline-flex min-h-[48px] items-center justify-center rounded-2xl px-4 text-center text-sm font-semibold transition ${base} ${className}`}
    >
      {children}
    </Link>
  );
};

export const StudentEmpty = ({ title, message, action }) => (
  <div className="rounded-[24px] border border-dashed border-stone-300 bg-stone-50 px-5 py-8 text-center">
    <p className="text-lg font-semibold text-stone-900">{title}</p>
    <p className="mt-2 text-sm leading-6 text-stone-600">{message}</p>
    {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
  </div>
);

export const StudentMainGrid = ({ primary, secondary, reverseOnMobile = false }) => (
  <div
    className={`grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px] ${
      reverseOnMobile ? 'grid-flow-row-dense' : ''
    }`}
  >
    <div className={`space-y-5 ${reverseOnMobile ? 'order-2 xl:order-1' : ''}`}>{primary}</div>
    <div className={`space-y-5 ${reverseOnMobile ? 'order-1 xl:order-2' : ''}`}>{secondary}</div>
  </div>
);
